import { createContext, useContext, useCallback, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const ConfirmContext = createContext(null);

// Custom promise-based confirm. Replaces the native window.confirm so the browser
// never shows its "prevent additional dialogs" (Suppress Dialogues) checkbox.
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [typed, setTyped] = useState("");
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || "Are you sure?",
      description: opts.description || "",
      confirmText: opts.confirmText || "OK",
      cancelText: opts.cancelText || "Cancel",
      danger: opts.danger ?? true,
      requireText: opts.requireText || null,
    });
    setTyped("");
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const close = (result) => {
    setState(null);
    setTyped("");
    if (resolver.current) { resolver.current(result); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(v) => { if (!v) close(false); }}>
        <DialogContent className="bg-white max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl text-[#2E2E2E]">{state?.title}</DialogTitle>
            {state?.description && <DialogDescription>{state.description}</DialogDescription>}
          </DialogHeader>
          {state?.requireText && (
            <div className="mt-1">
              <p className="text-sm text-[#2E2E2E] mb-2">
                Type <span className="font-black">{state.requireText}</span> to confirm.
              </p>
              <input
                type="text"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="confirm-require-input"
                placeholder={state.requireText}
                className="w-full h-12 px-4 rounded-[10px] border-2 border-border focus:border-[#E63946] outline-none font-semibold text-[#2E2E2E]"
              />
            </div>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <button type="button" data-testid="confirm-cancel" onClick={() => close(false)}
              className="h-12 px-5 rounded-full border-2 border-[#3BB54A] text-[#2E2E2E] font-bold">
              {state?.cancelText}
            </button>
            <button type="button" data-testid="confirm-ok" onClick={() => close(true)}
              disabled={!!state?.requireText && typed.trim() !== state.requireText}
              className={`h-12 px-5 rounded-full font-bold disabled:opacity-40 disabled:cursor-not-allowed ${state?.danger ? "bg-[#E63946] text-white" : "bg-[#3BB54A] text-white"}`}>
              {state?.confirmText}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
